/**
 * Repair Decisioning and Repairer Selection JavaScript
 * Handles the behavior of the repair decisioning and repairer selection accordions
 */

// Immediately hide the loading spinner when the page loads
document.addEventListener('DOMContentLoaded', function() {
    // Hide loading spinner initially and show no photos message
    const loadingElement = document.getElementById('repair-decisioning-loading');
    const noPhotosMessage = document.getElementById('repair-decisioning-no-photos');
    
    if (loadingElement) loadingElement.style.display = 'none';
    if (noPhotosMessage) noPhotosMessage.style.display = 'block';
});

const RepairDecisioning = {
    // Settings and state
    isAnalysisComplete: false,
    isConfirmed: false,
    aiDetermination: 'Repairable', // Default determination
    confidenceLevel: '80%',        // Default confidence level

    // Initialize the repair decisioning functionality
    init: function() {
        console.log('Initializing Repair Decisioning');
        
        // Directly add bootstrap collapse event listener
        const repairAccordionElement = document.getElementById('collapseRepairDecisioning');
        if (repairAccordionElement) {
            repairAccordionElement.addEventListener('show.bs.collapse', () => {
                console.log('Repair accordion opening via bootstrap event');
                this.handleAccordionOpen();
            });
        }
        
        // Also add click handler as backup
        const repairAccordionButton = document.querySelector('[data-bs-target="#collapseRepairDecisioning"]');
        if (repairAccordionButton) {
            repairAccordionButton.addEventListener('click', () => {
                console.log('Repair accordion button clicked');
                // Only react if the accordion is closed (collapsed)
                if (repairAccordionButton.classList.contains('collapsed')) {
                    // Wait a brief moment to allow bootstrap to start the animation
                    setTimeout(() => {
                        this.handleAccordionOpen();
                    }, 50);
                }
            });
        }

        // Event listener for the dropdown selection
        const repairDecisionDropdown = document.getElementById('repairDecisionDropdown');
        if (repairDecisionDropdown) {
            repairDecisionDropdown.addEventListener('change', (e) => {
                console.log('Dropdown changed:', e.target.value);
                this.updateDecision(e.target.value, true);
            });
        }

        // Add event listener for the confirm button
        const confirmBtn = document.getElementById('confirm-repair-decision');
        if (confirmBtn) {
            confirmBtn.addEventListener('click', () => {
                console.log('Confirm button clicked');
                this.confirmDecision();
            });
        }

        // Ensure accordion has status indicator
        this.initStatusIndicator();
        
        // Debug: Log file input element existence
        const fileInput = document.getElementById('documentUpload');
        console.log('File input found:', !!fileInput);
        
        // Debug: Log initial file status
        this.debugLogFileStatus();
    },
    
    // Debug function to log file status
    debugLogFileStatus: function() {
        const fileInput = document.getElementById('documentUpload');
        const filePreviews = document.getElementById('file-previews');
        const noFilesMessage = document.getElementById('no-files-message');
        
        console.log('DEBUG - File input:', fileInput ? `exists, files: ${fileInput.files ? fileInput.files.length : 'none'}` : 'not found');
        console.log('DEBUG - File previews:', filePreviews ? `exists, children: ${filePreviews.children.length}` : 'not found');
        console.log('DEBUG - No files message:', noFilesMessage ? `exists, display: ${noFilesMessage.style.display}` : 'not found');
    },

    // Handle accordion opening
    handleAccordionOpen: function() {
        this.debugLogFileStatus();
        
        if (!this.isAnalysisComplete) {
            // Only start analysis if not already completed
            this.checkForPhotosAndStartAnalysis();
        } else {
            // If analysis is already complete, ensure content is shown
            this.showAnalysisResults();
        }
    },

    // Initialize status indicator for the accordion
    initStatusIndicator: function() {
        if (window.ClaimAssistant && ClaimAssistant.accordionStatus) {
            // Add this section to ClaimAssistant's accordion status
            ClaimAssistant.accordionStatus['collapseRepairDecisioning'] = 'pending';
            
            // Update status indicator if ClaimAssistant has the method
            if (typeof ClaimAssistant.updateAccordionStatus === 'function') {
                ClaimAssistant.updateAccordionStatus('collapseRepairDecisioning', 'pending');
            }
        }
    },

    // Check if photos are uploaded before starting analysis
    checkForPhotosAndStartAnalysis: function() {
        console.log('Checking for photos before analysis...');
        
        // Manually check DocumentManager's uploadedFiles if available
        let hasPhotos = false;
        
        // Method 1: Check DocumentManager
        if (window.DocumentManager && DocumentManager.uploadedFiles) {
            hasPhotos = DocumentManager.uploadedFiles.length > 0;
            console.log('Using DocumentManager: hasPhotos =', hasPhotos);
        }
        
        // Method 2: Check the file input directly
        if (!hasPhotos) {
            const fileInput = document.getElementById('documentUpload');
            hasPhotos = fileInput && fileInput.files && fileInput.files.length > 0;
            console.log('Using file input check: hasPhotos =', hasPhotos);
        }
        
        // Method 3: Check if file previews container has children
        if (!hasPhotos) {
            const filePreviews = document.getElementById('file-previews');
            if (filePreviews) {
                // Filter out non-element nodes like text nodes
                const actualChildren = Array.from(filePreviews.children).filter(child => child.nodeType === 1);
                hasPhotos = actualChildren.length > 0;
                console.log('Using file previews check: hasPhotos =', hasPhotos, 'children:', actualChildren.length);
            }
        }
        
        // Method 4: Check if no-files message is hidden
        if (!hasPhotos) {
            const noFilesMessage = document.getElementById('no-files-message');
            hasPhotos = noFilesMessage && 
                        (noFilesMessage.style.display === 'none' || 
                         getComputedStyle(noFilesMessage).display === 'none');
            console.log('Using no-files message check: hasPhotos =', hasPhotos);
        }
        
        // IMPORTANT: For testing, you can force hasPhotos to true
        // Uncomment the line below to force the analysis to run regardless of photos
        // hasPhotos = true;
        
        console.log('Final photo check result: hasPhotos =', hasPhotos);
        
        if (hasPhotos) {
            this.startAnalysis();
        } else {
            // Show message that photos are needed
            this.showNoPhotosMessage();
        }
    },

    // Show message when no photos are uploaded
    showNoPhotosMessage: function() {
        console.log('No photos found, showing message');
        
        const loadingElement = document.getElementById('repair-decisioning-loading');
        const contentElement = document.getElementById('repair-decisioning-content');
        const noPhotosMessage = document.getElementById('repair-decisioning-no-photos');
        
        if (loadingElement) {
            loadingElement.style.display = 'none';
            console.log('Hidden loading element');
        }
        
        if (contentElement) {
            contentElement.style.display = 'none';
            console.log('Hidden content element');
        }
        
        if (noPhotosMessage) {
            noPhotosMessage.style.display = 'block';
            console.log('Shown no photos message');
        } else {
            console.error('No photos message element not found!');
        }
    },

    // Start the repair analysis with loading spinner
    startAnalysis: function() {
        console.log('Starting repair analysis with spinner');
        
        // Hide no photos message if it exists
        const noPhotosMessage = document.getElementById('repair-decisioning-no-photos');
        if (noPhotosMessage) {
            noPhotosMessage.style.display = 'none';
            console.log('Hidden no photos message');
        }
        
        // Show loading spinner, hide content
        const loadingElement = document.getElementById('repair-decisioning-loading');
        const contentElement = document.getElementById('repair-decisioning-content');
        
        if (loadingElement) {
            loadingElement.style.display = 'block';
            console.log('Shown loading element');
        } else {
            console.error('Loading element not found!');
        }
        
        if (contentElement) {
            contentElement.style.display = 'none';
            console.log('Hidden content element');
        }
            
        // Simulate analysis for 4 seconds
        console.log('Starting 4-second analysis timer at', new Date().toISOString());
        setTimeout(() => {
            console.log('4-second timer completed at', new Date().toISOString());
            this.completeAnalysis();
        }, 4000);
    },

    // Show analysis results (used when reopening accordion after analysis is already complete)
    showAnalysisResults: function() {
        console.log('Showing existing analysis results');
        
        const loadingElement = document.getElementById('repair-decisioning-loading');
        const contentElement = document.getElementById('repair-decisioning-content');
        const noPhotosMessage = document.getElementById('repair-decisioning-no-photos');
        
        if (noPhotosMessage) {
            noPhotosMessage.style.display = 'none';
            console.log('Hidden no photos message');
        }
        
        if (loadingElement) {
            loadingElement.style.display = 'none';
            console.log('Hidden loading element');
        }
        
        if (contentElement) {
            contentElement.style.display = 'flex';
            console.log('Shown content element');
        } else {
            console.error('Content element not found!');
        }
    },

    // Complete the analysis and show results
    completeAnalysis: function() {
        console.log('Analysis complete, showing results');
        
        // Hide loading spinner, show content
        const loadingElement = document.getElementById('repair-decisioning-loading');
        const contentElement = document.getElementById('repair-decisioning-content');
        
        if (loadingElement) {
            loadingElement.style.display = 'none';
            console.log('Hidden loading element');
        }
        
        if (contentElement) {
            contentElement.style.display = 'flex';
            console.log('Shown content element');
            
            // Update UI with AI results
            const aiDeterminationElement = document.getElementById('ai-determination');
            const aiConfidenceElement = document.getElementById('ai-confidence');
            
            if (aiDeterminationElement) {
                aiDeterminationElement.textContent = this.aiDetermination;
                console.log('Set AI determination text to:', this.aiDetermination);
            }
            
            if (aiConfidenceElement) {
                aiConfidenceElement.textContent = this.confidenceLevel;
                console.log('Set AI confidence text to:', this.confidenceLevel);
            }
            
            // Set the dropdown to match AI result
            const repairDecisionDropdown = document.getElementById('repairDecisionDropdown');
            if (repairDecisionDropdown) {
                for (let i = 0; i < repairDecisionDropdown.options.length; i++) {
                    if (repairDecisionDropdown.options[i].value === this.aiDetermination) {
                        repairDecisionDropdown.selectedIndex = i;
                        console.log('Set dropdown selection to index:', i);
                        break;
                    }
                }
            }
            
            this.isAnalysisComplete = true;
            console.log('Analysis marked as complete');
            
            // Update accordion status to review (needs confirmation)
            if (window.ClaimAssistant && typeof ClaimAssistant.updateAccordionStatus === 'function') {
                ClaimAssistant.updateAccordionStatus('collapseRepairDecisioning', 'review');
                console.log('Updated accordion status to review');
            }
        } else {
            console.error('Content element not found!');
        }
    },

    // Update the decision dropdown
    updateDecision: function(value, userInitiated = true) {
        console.log('Updating decision to:', value);
        
        if (userInitiated && value !== this.aiDetermination) {
            // If user changed from AI recommendation, highlight it
            const repairDecisionDropdown = document.getElementById('repairDecisionDropdown');
            if (repairDecisionDropdown) {
                repairDecisionDropdown.classList.add('text-warning', 'border-warning');
                console.log('Added warning styling to dropdown');
            }
        } else {
            // Reset styling if matching AI recommendation
            const repairDecisionDropdown = document.getElementById('repairDecisionDropdown');
            if (repairDecisionDropdown) {
                repairDecisionDropdown.classList.remove('text-warning', 'border-warning');
                console.log('Removed warning styling from dropdown');
            }
        }
    },

    // Confirm the repair decision
    confirmDecision: function() {
        console.log('Decision confirmed');
        this.isConfirmed = true;
        
        // Update accordion status to done
        if (window.ClaimAssistant && typeof ClaimAssistant.updateAccordionStatus === 'function') {
            ClaimAssistant.updateAccordionStatus('collapseRepairDecisioning', 'done');
            console.log('Updated accordion status to done');
        }
        
        // Show repairer selection accordion
        RepairerSelection.showAccordion();
        
        // Collapse this accordion
        const accordionButton = document.querySelector('[data-bs-target="#collapseRepairDecisioning"]');
        if (accordionButton && !accordionButton.classList.contains('collapsed')) {
            setTimeout(() => {
                accordionButton.click();
                console.log('Collapsed repair decisioning accordion');
            }, 500);
        }
        
        // Open the repairer selection accordion
        setTimeout(() => {
            const repairerAccordionButton = document.querySelector('[data-bs-target="#collapseRepairerSelection"]');
            if (repairerAccordionButton && repairerAccordionButton.classList.contains('collapsed')) {
                repairerAccordionButton.click();
                console.log('Opened repairer selection accordion');
            }
        }, 800);
    }
};

const RepairerSelection = {
    // Settings and state
    isConfirmed: false,
    selectedRepairer: null,
    
    // Initialize the repairer selection functionality
    init: function() {
        console.log('Initializing Repairer Selection');
        
        // Add event listener for the repairer dropdown
        const repairerSelect = document.getElementById('repairerSelect');
        if (repairerSelect) {
            repairerSelect.addEventListener('change', (e) => {
                this.selectRepairer(e.target.value);
            });
        }
        
        // Add event listener for confirm button
        const confirmBtn = document.getElementById('confirm-repairer-selection');
        if (confirmBtn) {
            confirmBtn.addEventListener('click', () => {
                this.confirmSelection();
            });
        }
        
        // Initialize map zoom functionality
        this.initMapZoom();
        
        // Hide all repairer pins initially
        this.updatePins();
        
        // Ensure accordion has status indicator
        this.initStatusIndicator();
    },

    // Initialize status indicator for the accordion
    initStatusIndicator: function() {
        if (window.ClaimAssistant && ClaimAssistant.accordionStatus) {
            // Add this section to ClaimAssistant's accordion status
            ClaimAssistant.accordionStatus['collapseRepairerSelection'] = 'pending';
            
            // Update status indicator if ClaimAssistant has the method
            if (typeof ClaimAssistant.updateAccordionStatus === 'function') {
                ClaimAssistant.updateAccordionStatus('collapseRepairerSelection', 'pending');
            }
        }
    },

    // Show the accordion (called after repair decision is confirmed)
    showAccordion: function() {
        const accordionItem = document.getElementById('repairer-selection-accordion');
        if (accordionItem) {
            accordionItem.style.display = 'block';
            console.log('Repairer selection accordion is now visible');
        }
    },

    // Handle repairer selection
    selectRepairer: function(repairerId) {
        console.log('Selected repairer:', repairerId);
        this.selectedRepairer = repairerId;
        
        // Update pins visibility
        this.updatePins();
        
        // Update location text
        const locationText = document.getElementById('repairer-location-text');
        if (locationText) {
            if (repairerId) {
                // Get the selected option's text
                const repairerSelect = document.getElementById('repairerSelect');
                const selectedOption = repairerSelect.options[repairerSelect.selectedIndex];
                locationText.textContent = selectedOption.text;
            } else {
                locationText.textContent = 'No repairer selected';
            }
        }
        
        // Update accordion status to review (needs confirmation)
        if (window.ClaimAssistant && typeof ClaimAssistant.updateAccordionStatus === 'function') {
            ClaimAssistant.updateAccordionStatus('collapseRepairerSelection', repairerId ? 'review' : 'pending');
        }
    },

    // Update repairer pins on the map
    updatePins: function() {
        const pins = document.querySelectorAll('.repairer-pin');
        pins.forEach(pin => {
            const pinRepairerId = pin.getAttribute('data-repairer');
            if (this.selectedRepairer) {
                // If we have a selection, only show the selected pin
                pin.style.display = (pinRepairerId === this.selectedRepairer) ? 'block' : 'none';
            } else {
                // If no selection, show all pins
                pin.style.display = 'block';
            }
        });
    },

    // Confirm repairer selection
    confirmSelection: function() {
        if (!this.selectedRepairer) {
            // Show error if no repairer selected
            alert('Please select a repairer before confirming');
            return;
        }
        
        console.log('Repairer selection confirmed:', this.selectedRepairer);
        this.isConfirmed = true;
        
        // Update accordion status to done
        if (window.ClaimAssistant && typeof ClaimAssistant.updateAccordionStatus === 'function') {
            ClaimAssistant.updateAccordionStatus('collapseRepairerSelection', 'done');
        }
        
        // Collapse this accordion
        const accordionButton = document.querySelector('[data-bs-target="#collapseRepairerSelection"]');
        if (accordionButton && !accordionButton.classList.contains('collapsed')) {
            setTimeout(() => {
                accordionButton.click();
                console.log('Collapsed repairer selection accordion');
            }, 500);
        }
    },

    // Initialize map zoom functionality
    initMapZoom: function() {
        const mapImage = document.querySelector('#repairerMap .map-image');
        const zoomIn = document.querySelector('#repairerMap .zoom-in');
        const zoomOut = document.querySelector('#repairerMap .zoom-out');

        if (!mapImage || !zoomIn || !zoomOut) return;

        let currentZoom = 1;

        zoomIn.addEventListener('click', function() {
            currentZoom = Math.min(currentZoom + 0.2, 2.0);
            mapImage.style.transform = `scale(${currentZoom})`;
        });

        zoomOut.addEventListener('click', function() {
            currentZoom = Math.max(currentZoom - 0.2, 1.0);
            mapImage.style.transform = `scale(${currentZoom})`;
        });
    }
};

// Add document-level event listener for file uploads to trigger reevaluation of the repair decisioning
document.addEventListener('change', function(event) {
    // If this is the file upload input and files were added
    if (event.target.id === 'documentUpload' && event.target.files && event.target.files.length > 0) {
        console.log('Files uploaded, resetting repair decisioning status');
        // Reset the analysis status so it will run again when the accordion is opened
        RepairDecisioning.isAnalysisComplete = false;
    }
});

// Force reset repair decisioning on page load to ensure it starts in a clean state
document.addEventListener('DOMContentLoaded', function() {
    console.log('DOM loaded, initializing repair decisioning in clean state');
    // Reset the repair decisioning state
    if (RepairDecisioning) {
        RepairDecisioning.isAnalysisComplete = false;
    }
    
    // Initialize repair decisioning
    RepairDecisioning.init();
    
    // Initialize repairer selection
    RepairerSelection.init();
});